import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { withApiPrefix } from '../services/api';
import pageStyles from './history/HistoryPage.module.css';
import { AlertIcon, BookIcon, EditIcon, HeartIcon, PlayIcon } from '../components/Icons';
import HistoryMenu from './history/HistoryMenu';
import HistoryDetailView from './history/HistoryDetailView';
import FavoriteBooksView from './history/FavoriteBooksView';
import FavoriteWordsView from './history/FavoriteWordsView';
import FavoritePhrasesView from './history/FavoritePhrasesView';
import FavoriteSentencesView from './history/FavoriteSentencesView';
import FavoriteNotesView from './history/FavoriteNotesView';

const OSS_BASE_URL = 'https://resourcessin.oss-ap-southeast-1.aliyuncs.com';
const CLIP_END_TOLERANCE = 0.12;

const parseTimestamp = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatClipLabel = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return '00:00';
  }
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const History = ({ initialView = 'menu' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState(initialView || 'menu'); // 'menu' | 'books' | 'words' | 'sentences' | 'notes'
  useEffect(() => {
    if (initialView && initialView !== activeView) {
      setActiveView(initialView);
    }
  }, [initialView]);

  const [favoriteBooks, setFavoriteBooks] = useState([]);
  const [favoriteWords, setFavoriteWords] = useState([]);
  const [favoritePhrases, setFavoritePhrases] = useState([]);
  const [favoriteSentences, setFavoriteSentences] = useState([]);
  const [favoriteNotes, setFavoriteNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chapterMetadata, setChapterMetadata] = useState({});
  const [chapterMetadataLoading, setChapterMetadataLoading] = useState(false);
  const [chapterMetadataError, setChapterMetadataError] = useState(null);
  const [sentenceExpandedBooks, setSentenceExpandedBooks] = useState({});
  const [noteExpandedBooks, setNoteExpandedBooks] = useState({});
  const [bookMetadata, setBookMetadata] = useState({});
  const [bookMetadataLoading, setBookMetadataLoading] = useState(false);
  const [bookMetadataError, setBookMetadataError] = useState(null);
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalWords: 0,
    totalPhrases: 0,
    totalSentences: 0,
    totalNotes: 0
  });
  const [playingClip, setPlayingClip] = useState({ en: null, cn: null });
  const audioRefs = useRef({
    en: typeof Audio !== 'undefined' ? new Audio() : null,
    cn: typeof Audio !== 'undefined' ? new Audio() : null,
  });
  const audioChapterRef = useRef({ en: null, cn: null });
  const audioTimeUpdateRefs = useRef({ en: null, cn: null });
  const playbackTokenRef = useRef({ en: 0, cn: 0 });
  const [completedChapterCounts, setCompletedChapterCounts] = useState({});
  const cardConfigs = useMemo(() => ([
    {
      key: 'books',
      title: '收藏书籍',
      count: stats.totalBooks,
      icon: <BookIcon size={28} />,
    },
    {
      key: 'words',
      title: '收藏单词',
      count: stats.totalWords,
      icon: <HeartIcon size={28} />,
    },
    {
      key: 'phrases',
      title: '收藏短语',
      count: stats.totalPhrases,
      icon: <HeartIcon size={28} />,
    },
    {
      key: 'sentences',
      title: '收藏句子',
      count: stats.totalSentences,
      icon: <PlayIcon size={28} />,
    },
    {
      key: 'notes',
      title: '学习笔记',
      count: stats.totalNotes,
      icon: <EditIcon size={28} />,
    },
  ]), [stats]);
  const activeCard = useMemo(
    () => cardConfigs.find((card) => card.key === activeView),
    [cardConfigs, activeView]
  );

  const ensureAudioMetadata = useCallback((audio) => {
    if (!audio) return Promise.resolve();
    if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handleLoaded = () => {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      };
      const handleError = () => {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      };
      audio.addEventListener('loadedmetadata', handleLoaded, { once: true });
      audio.addEventListener('error', handleError, { once: true });
      try {
        audio.load();
      } catch (error) {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      }
    });
  }, []);

  const seekAudioTime = useCallback((audio, time) => {
    if (!audio) return Promise.resolve();
    const targetTime = Number.isFinite(time) ? Math.max(time, 0) : 0;
    if (Math.abs(audio.currentTime - targetTime) < 0.05) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handleSeeked = () => {
        audio.removeEventListener('seeked', handleSeeked);
        resolve();
      };
      audio.addEventListener('seeked', handleSeeked, { once: true });
      try {
        audio.currentTime = targetTime;
      } catch (error) {
        audio.removeEventListener('seeked', handleSeeked);
        resolve();
      }
    });
  }, []);

  const stopClip = useCallback((lang, resetToken = false) => {
    const audio = audioRefs.current[lang];
    if (!audio) return;
    const handler = audioTimeUpdateRefs.current[lang];
    if (handler) {
      audio.removeEventListener('timeupdate', handler);
      audioTimeUpdateRefs.current[lang] = null;
    }
    if (resetToken) {
      playbackTokenRef.current[lang] += 1;
    }
    try {
      audio.pause();
    } catch (error) {
      // ignore pause failure
    }
    setPlayingClip((prev) => {
      if (prev[lang] === null) {
        return prev;
      }
      return { ...prev, [lang]: null };
    });
  }, []);

  const playSentenceClip = useCallback(async (sentence, lang) => {
    if (!sentence) return;
    const audio = audioRefs.current[lang];
    if (!audio) return;

    const chapterId = sentence.chapter_id;
    if (chapterId === null || chapterId === undefined) {
      return;
    }

    const startKey = lang === 'en' ? 'en_start_timestamp' : 'cn_start_timestamp';
    const endKey = lang === 'en' ? 'en_end_timestamp' : 'cn_end_timestamp';
    const start = parseTimestamp(sentence[startKey]);
    const end = parseTimestamp(sentence[endKey]);

    if (start === null || end === null || end - start <= 0.05) {
      return;
    }

    playbackTokenRef.current[lang] += 1;
    const token = playbackTokenRef.current[lang];

    stopClip('en');
    stopClip('cn');

    const expectedSrc = `${OSS_BASE_URL}/${chapterId}/${lang}.mp3`;
    if (audioChapterRef.current[lang] !== chapterId || audio.src !== expectedSrc) {
      audioChapterRef.current[lang] = chapterId;
      audio.src = expectedSrc;
    }

    await ensureAudioMetadata(audio);
    if (playbackTokenRef.current[lang] !== token) {
      return;
    }

    await seekAudioTime(audio, start);
    if (playbackTokenRef.current[lang] !== token) {
      return;
    }

    const handleTimeUpdate = () => {
      if (audio.currentTime >= end - CLIP_END_TOLERANCE) {
        stopClip(lang, true);
      }
    };

    audioTimeUpdateRefs.current[lang] = handleTimeUpdate;
    audio.addEventListener('timeupdate', handleTimeUpdate);

    try {
      await audio.play();
      if (playbackTokenRef.current[lang] !== token) {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        if (audioTimeUpdateRefs.current[lang] === handleTimeUpdate) {
          audioTimeUpdateRefs.current[lang] = null;
        }
        return;
      }
      setPlayingClip((prev) => ({ ...prev, [lang]: sentence.id }));
    } catch (error) {
      console.error('播放音频失败:', error);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      if (audioTimeUpdateRefs.current[lang] === handleTimeUpdate) {
        audioTimeUpdateRefs.current[lang] = null;
      }
      stopClip(lang, true);
    }
  }, [ensureAudioMetadata, seekAudioTime, stopClip]);

  useEffect(() => {
    const { en, cn } = audioRefs.current;
    [en, cn].forEach((audio) => {
      if (audio) {
        audio.preload = 'auto';
      }
    });

    return () => {
      ['en', 'cn'].forEach((lang) => {
        const audio = audioRefs.current[lang];
        const handler = audioTimeUpdateRefs.current[lang];
        if (audio && handler) {
          audio.removeEventListener('timeupdate', handler);
          audioTimeUpdateRefs.current[lang] = null;
        }
        if (audio) {
          try {
            audio.pause();
          } catch (error) {
            // ignore
          }
          audio.src = '';
        }
        audioChapterRef.current[lang] = null;
      });
    };
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setChapterMetadataError(null);

      // 并行获取收藏的书籍、单词、短语、句子和笔记
      const [booksResponse, wordsResponse, phrasesResponse, sentencesResponse, notesResponse, completedResponse] = await Promise.all([
        api.get('/favorite-books/').catch(() => ({ data: [] })),
        api.get('/favorite-words/').catch(() => ({ data: [] })),
        api.get('/favorite-phrases').catch(() => ({ data: [] })),
        api.get(withApiPrefix('favorite-sentences/')).catch(() => ({ data: [] })),
        api.get('/user-notes/').catch(() => ({ data: [] })),
        api.get('/completed-chapters/').catch(() => ({ data: [] })),
      ]);

      setFavoriteBooks(booksResponse.data || []);
      setFavoriteWords(wordsResponse.data || []);
      setFavoritePhrases(phrasesResponse.data || []);
      setFavoriteSentences(sentencesResponse.data || []);
      setFavoriteNotes(notesResponse.data || []);

      const completedEntries = Array.isArray(completedResponse.data) ? completedResponse.data : [];
      const countsMap = completedEntries.reduce((acc, entry) => {
        if (entry?.novel_id == null) return acc;
        const key = String(entry.novel_id);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      setCompletedChapterCounts(countsMap);

      // 设置统计信息
      setStats({
        totalBooks: booksResponse.data?.length || 0,
        totalWords: wordsResponse.data?.length || 0,
        totalPhrases: phrasesResponse.data?.length || 0,
        totalSentences: sentencesResponse.data?.length || 0,
        totalNotes: notesResponse.data?.length || 0
      });

    } catch (error) {
      console.error('获取收藏数据失败:', error);
      if (error.response?.status === 401) {
        setError('请先登录查看收藏内容');
      } else {
        setError('获取收藏数据失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  useEffect(() => {
    const state = location.state || {};
    if (!state.historyView && !state.refreshFavorites) {
      return;
    }

    if (state.historyView?.activeView) {
      setActiveView(state.historyView.activeView);
    }
    if (state.refreshFavorites) {
      fetchFavorites();
    }
    navigate(`${location.pathname}${location.search || ''}${location.hash || ''}`, { replace: true });
  }, [location, navigate, fetchFavorites]);

  const pendingChapterIds = useMemo(() => {
    if (!favoriteWords.length && !favoritePhrases.length && !favoriteSentences.length && !favoriteNotes.length) {
      return [];
    }
    const missing = new Set();
    favoriteWords.forEach((word) => {
      const rawId = word.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const chapterId = String(rawId);
      if (!chapterMetadata[chapterId]) {
        missing.add(chapterId);
      }
    });
    favoritePhrases.forEach((phrase) => {
      const rawId = phrase.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const chapterId = String(rawId);
      if (!chapterMetadata[chapterId]) {
        missing.add(chapterId);
      }
    });
    favoriteSentences.forEach((sentence) => {
      const rawId = sentence.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const chapterId = String(rawId);
      if (!chapterMetadata[chapterId]) {
        missing.add(chapterId);
      }
    });
    favoriteNotes.forEach((note) => {
      const rawId = note.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const chapterId = String(rawId);
      if (!chapterMetadata[chapterId]) {
        missing.add(chapterId);
      }
    });
    return Array.from(missing);
  }, [favoriteWords, favoritePhrases, favoriteSentences, favoriteNotes, chapterMetadata]);

  const pendingBookIds = useMemo(() => {
    if (!favoriteBooks.length) {
      return [];
    }
    const missing = new Set();
    favoriteBooks.forEach((item) => {
      const key = String(item.novel_id);
      if (!bookMetadata[key]) {
        missing.add(item.novel_id);
      }
    });
    return Array.from(missing);
  }, [favoriteBooks, bookMetadata]);

  useEffect(() => {
    if (!pendingChapterIds.length) {
      setChapterMetadataLoading(false);
      return;
    }

    let isCancelled = false;

    const loadChapterMetadata = async () => {
      setChapterMetadataLoading(true);
      setChapterMetadataError(null);

      const responses = await Promise.allSettled(
        pendingChapterIds.map(async (chapterId) => {
          try {
            const response = await api.get(`/chapters/${chapterId}`);
            const chapter = response.data;

            let displayTitle = chapter.title;
            try {
              const contentData = JSON.parse(chapter.content);
              if (contentData?.chapter_title) {
                displayTitle = contentData.chapter_title;
              }
            } catch (e) {
              // JSON 格式不符合预期时忽略
            }

            const volumeInfo = chapter.volume || {};
            const novelInfo = chapter.novel || volumeInfo.novel || {};

            const volumeId = chapter.volume_id ?? volumeInfo.id;
            const volumeTitle = chapter.volume_title || volumeInfo.title;
            const volumeOrder = chapter.volume_order_index ?? volumeInfo.order_index;
            const novelId = chapter.novel_id ?? novelInfo.id;
            const novelTitle = chapter.novel_title || novelInfo.title;

            return {
              chapterId,
              data: {
                id: chapter.id,
                title: displayTitle || chapter.title,
                orderIndex: chapter.order_index,
                rawTitle: chapter.title,
                volumeId,
                volumeTitle: volumeTitle || '未命名卷',
                volumeOrder,
                novelId,
                novelTitle: novelTitle || '未命名书籍',
              }
            };
          } catch (error) {
            console.error('加载章节信息失败:', error);
            throw error;
          }
        })
      );

      if (isCancelled) {
        return;
      }

      const newEntries = {};
      let hasError = false;

      responses.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.data) {
          const key = String(result.value.chapterId);
          newEntries[key] = result.value.data;
        } else {
          hasError = true;
        }
      });

      if (Object.keys(newEntries).length) {
        setChapterMetadata((prev) => ({ ...prev, ...newEntries }));
      }

      if (hasError) {
        setChapterMetadataError('部分章节信息加载失败');
      }

      setChapterMetadataLoading(false);
    };

    loadChapterMetadata();

    return () => {
      isCancelled = true;
    };
  }, [pendingChapterIds]);

  useEffect(() => {
    if (!pendingBookIds.length) {
      setBookMetadataLoading(false);
      return;
    }

    let isCancelled = false;

    const loadBookMetadata = async () => {
      setBookMetadataLoading(true);
      setBookMetadataError(null);
      const responses = await Promise.allSettled(
        pendingBookIds.map(async (novelId) => {
          try {
            const response = await api.get(`/novels/${novelId}`);
            return { novelId, data: response.data };
          } catch (error) {
            console.error('加载书籍信息失败:', error);
            throw error;
          }
        })
      );

      if (isCancelled) {
        return;
      }

      const newEntries = {};
      let hasError = false;

      responses.forEach((result) => {
        if (result.status === 'fulfilled') {
          const key = String(result.value.novelId);
          newEntries[key] = result.value.data;
        } else {
          hasError = true;
        }
      });

      if (Object.keys(newEntries).length) {
        setBookMetadata((prev) => ({ ...prev, ...newEntries }));
      }

      if (hasError) {
        setBookMetadataError('部分书籍信息加载失败');
      }

      setBookMetadataLoading(false);
    };

    loadBookMetadata();

    return () => {
      isCancelled = true;
    };
  }, [pendingBookIds]);

  const buildGroupedData = useCallback((items, itemAccessor, metadataPending) => {
    if (!items.length) {
      return [];
    }

    const defaultSortValue = Number.MAX_SAFE_INTEGER;
    const bookMap = new Map();

    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : defaultSortValue;
    };

    items.forEach((item) => {
      const chapterIdKey = String(item.chapter_id ?? '');
      const meta = chapterMetadata[chapterIdKey];

      const bookId = meta?.novelId ?? 'unknown-book';
      const bookTitle = meta?.novelTitle || (metadataPending ? '加载中…' : '未分类书籍');
      const bookKey = `${bookId}`;

      if (!bookMap.has(bookKey)) {
        bookMap.set(bookKey, {
          key: bookKey,
          bookId,
          bookTitle,
          itemCount: 0,
          items: [],
        });
      }

      const bookGroup = bookMap.get(bookKey);
      bookGroup.itemCount += 1;
      bookGroup.items.push(itemAccessor(item));
    });

    const sortedBooks = Array.from(bookMap.values())
      .map((bookGroup) => {
        const itemsWithOrder = [...bookGroup.items].sort((a, b) => {
          const metaA = chapterMetadata[String(a.chapter_id ?? '')];
          const metaB = chapterMetadata[String(b.chapter_id ?? '')];

          const volumeOrderA = metaA?.volumeOrder ?? defaultSortValue;
          const volumeOrderB = metaB?.volumeOrder ?? defaultSortValue;
          if (volumeOrderA !== volumeOrderB) {
            return volumeOrderA - volumeOrderB;
          }

          const chapterOrderA = metaA?.orderIndex ?? defaultSortValue;
          const chapterOrderB = metaB?.orderIndex ?? defaultSortValue;
          if (chapterOrderA !== chapterOrderB) {
            return chapterOrderA - chapterOrderB;
          }

          const sentenceOrderA = toNumber(a.sentence_id);
          const sentenceOrderB = toNumber(b.sentence_id);
          if (sentenceOrderA !== sentenceOrderB) {
            return sentenceOrderA - sentenceOrderB;
          }

          const idA = toNumber(a.id);
          const idB = toNumber(b.id);
          if (idA !== idB) {
            return idA - idB;
          }

          return 0;
        });

        return {
          ...bookGroup,
          items: itemsWithOrder,
        };
      })
      .sort((a, b) => a.bookTitle.localeCompare(b.bookTitle, 'zh-Hans-CN-u-co-pinyin', { sensitivity: 'base' }));

    return sortedBooks;
  }, [chapterMetadata]);

  const isChapterMetadataPending = chapterMetadataLoading || pendingChapterIds.length > 0;

  const groupedFavoriteWords = useMemo(
    () => buildGroupedData(favoriteWords, word => word, isChapterMetadataPending),
    [favoriteWords, buildGroupedData, isChapterMetadataPending]
  );
  const groupedFavoritePhrases = useMemo(
    () => buildGroupedData(favoritePhrases, phrase => phrase, isChapterMetadataPending),
    [favoritePhrases, buildGroupedData, isChapterMetadataPending]
  );
  const groupedFavoriteSentences = useMemo(
    () => buildGroupedData(favoriteSentences, sentence => sentence, isChapterMetadataPending),
    [favoriteSentences, buildGroupedData, isChapterMetadataPending]
  );
  const groupedFavoriteNotes = useMemo(
    () => buildGroupedData(favoriteNotes, note => note, isChapterMetadataPending),
    [favoriteNotes, buildGroupedData, isChapterMetadataPending]
  );

  useEffect(() => {
    const syncExpandedState = (groupedData, setBooksState) => {
      if (!groupedData.length) {
        setBooksState({});
        return;
      }

      const validBookKeys = new Set(groupedData.map(book => book.key));

      setBooksState(prev => {
        let changed = false;
        const next = {};
        Object.keys(prev || {}).forEach(key => {
          if (validBookKeys.has(key)) {
            next[key] = prev[key];
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };

    syncExpandedState(groupedFavoriteSentences, setSentenceExpandedBooks);
    syncExpandedState(groupedFavoriteNotes, setNoteExpandedBooks);
  }, [groupedFavoriteWords, groupedFavoriteSentences, groupedFavoriteNotes]);

  const handleDeleteSentence = async (sentenceId) => {
    try {
      await api.delete(withApiPrefix(`favorite-sentences/${sentenceId}`));
      setFavoriteSentences(prev => prev.filter(sentence => sentence.id !== sentenceId));
      setStats(prev => ({ ...prev, totalSentences: Math.max(0, prev.totalSentences - 1) }));
      console.log('删除收藏句子成功');
    } catch (error) {
      console.error('删除收藏句子失败:', error);
      alert('删除失败，请稍后重试');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/user-notes/${noteId}`);
      setFavoriteNotes(prev => prev.filter(note => note.id !== noteId));
      setStats(prev => ({ ...prev, totalNotes: Math.max(0, prev.totalNotes - 1) }));
      console.log('删除收藏笔记成功');
    } catch (error) {
      console.error('删除收藏笔记失败:', error);
      alert('删除失败，请稍后重试');
    }
  };

  const handleDeleteBook = async (favoriteId) => {
    try {
      await api.delete(`/favorite-books/${favoriteId}`);
      setFavoriteBooks((prev) => prev.filter((item) => item.id !== favoriteId));
      setStats((prev) => ({ ...prev, totalBooks: Math.max(0, prev.totalBooks - 1) }));
    } catch (error) {
      console.error('删除收藏书籍失败:', error);
      alert('取消收藏失败，请稍后重试');
    }
  };

  // const renderWithBold = useCallback((text) => {
  //   if (!text) return text;
  //   return text
  //     .split(/(\*\*[\s\S]*?\*\*)/g)
  //     .filter(Boolean)
  //     .map((segment, idx) => {
  //       if (segment.startsWith('**') && segment.endsWith('**')) {
  //         const inner = segment.slice(2, -2);
  //         return (
  //           <strong key={`note-bold-${idx}`} className={notesStyles.noteEmphasis}>
  //             {inner}
  //           </strong>
  //         );
  //       }
  //       return <React.Fragment key={`note-text-${idx}`}>{segment}</React.Fragment>;
  //     });
  // }, []);

  const handleOpenBookWords = (bookGroup) => {
    if (!bookGroup?.items?.length) {
      return;
    }
    const bookMeta = bookMetadata[String(bookGroup.bookId)] || {};
    navigate(`/history/words/${encodeURIComponent(bookGroup.key)}`, {
      state: {
        bookGroup: {
          key: String(bookGroup.key),
          bookId: bookGroup.bookId,
          bookTitle: bookMeta.title || bookGroup.bookTitle || '未命名书籍',
          items: bookGroup.items,
          itemCount: bookGroup.itemCount,
        },
        bookMeta,
      },
    });
  };

  const handleOpenBookPhrases = (bookGroup) => {
    if (!bookGroup?.items?.length) {
      return;
    }
    const bookMeta = bookMetadata[String(bookGroup.bookId)] || {};
    navigate(`/history/phrases/${encodeURIComponent(bookGroup.key)}`, {
      state: {
        bookGroup: {
          key: String(bookGroup.key),
          bookId: bookGroup.bookId,
          bookTitle: bookMeta.title || bookGroup.bookTitle || '未命名书籍',
          items: bookGroup.items,
          itemCount: bookGroup.itemCount,
        },
        bookMeta,
      },
    });
  };



  const toggleSentenceBookGroup = (bookKey) => {
    setSentenceExpandedBooks(prev => {
      const next = { ...(prev || {}) };
      if (next[bookKey]) {
        delete next[bookKey];
      } else {
        next[bookKey] = true;
      }
      return next;
    });
  };

  const handleStartSentenceStudy = useCallback((bookGroup) => {
    if (!bookGroup?.bookId) {
      return;
    }

    navigate(`/learn/sentences/${bookGroup.bookId}`, {
      state: {
        from: 'historySentences',
        historyReturn: {
          activeView: 'sentences',
        },
        sentences: bookGroup.items,
      },
    });
  }, [navigate]);

  const toggleNoteBookGroup = (bookKey) => {
    setNoteExpandedBooks(prev => {
      const next = { ...(prev || {}) };
      if (next[bookKey]) {
        delete next[bookKey];
      } else {
        next[bookKey] = true;
      }
      return next;
    });
  };

  const handleStartNoteStudy = useCallback((bookGroup) => {
    if (!bookGroup?.bookId) {
      return;
    }

    navigate(`/learn/notes/${bookGroup.bookId}`, {
      state: {
        from: 'historyNotes',
        historyReturn: {
          activeView: 'notes',
        },
        notes: bookGroup.items,
      },
    });
  }, [navigate]);



  if (loading) {
    return (
      <div className={pageStyles.history}>
        <div className={pageStyles.historyLoading}>
          <div className={pageStyles.loadingSpinner}></div>
          <p>加载收藏内容中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageStyles.history}>
        <div className={pageStyles.noHistory}>
          <div className={pageStyles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button 
            className={pageStyles.retryBtn}
            onClick={fetchFavorites}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={pageStyles.history}>
      {activeView === 'menu' ? (
        <HistoryMenu
          cardConfigs={cardConfigs}
          onSelectView={(card) => {
            const targetPath = card.key === 'books' ? '/history/novels' : `/history/${card.key}`;
            setActiveView(card.key);
            navigate(targetPath, {
              state: {
                historyView: { activeView: card.key },
              },
            });
          }}
        />
      ) : (
        <HistoryDetailView
          title={activeCard?.title}
          onBack={() => {
            setActiveView('menu');
            if (location.pathname !== '/history') {
              navigate('/history');
            }
          }}
        >
          {activeView === 'books' && (
            <FavoriteBooksView
              books={favoriteBooks}
              bookMetadata={bookMetadata}
              completedChapterCounts={completedChapterCounts}
              loading={bookMetadataLoading}
              error={bookMetadataError}
              onDeleteBook={handleDeleteBook}
              onOpenBookDetail={(favoriteBook) =>
                navigate(`/book/${favoriteBook.novel_id}`, { state: { from: '/history/novels' } })
              }
            />
          )}

          {activeView === 'words' && (
            <FavoriteWordsView
              groups={groupedFavoriteWords}
              bookMetadata={bookMetadata}
              onOpenBookWords={handleOpenBookWords}
            />
          )}

          {activeView === 'phrases' && (
            <FavoritePhrasesView
              groups={groupedFavoritePhrases}
              bookMetadata={bookMetadata}
              onOpenBookPhrases={handleOpenBookPhrases}
            />
          )}

          {activeView === 'sentences' && (
            <FavoriteSentencesView
              groups={groupedFavoriteSentences}
              expandedState={sentenceExpandedBooks}
              onToggleGroup={toggleSentenceBookGroup}
              onPlayClip={playSentenceClip}
              onStopClip={(lang) => stopClip(lang, true)}
              playingClip={playingClip}
              onDeleteSentence={handleDeleteSentence}
              onStartSentenceStudy={handleStartSentenceStudy}
              loading={chapterMetadataLoading}
              error={chapterMetadataError}
              emptyCard={(
                <div className={pageStyles.noHistory}>
                  <div className={pageStyles.noHistoryIcon}><EditIcon size={48} /></div>
                  <h3>还没有收藏的句子</h3>
                  <p>在阅读时点击句子前的收藏按钮即可收藏句子</p>
                </div>
              )}
            />
          )}

          {activeView === 'notes' && (
            <FavoriteNotesView
              groups={groupedFavoriteNotes}
              expandedState={noteExpandedBooks}
              onToggleGroup={toggleNoteBookGroup}
              onDeleteNote={handleDeleteNote}
              onStartNoteStudy={handleStartNoteStudy}
              // onGoToChapter={handleGoToChapterWithHighlight}
              // renderWithBold={renderWithBold}
              loading={chapterMetadataLoading}
              error={chapterMetadataError}
              emptyCard={(
                <div className={pageStyles.noHistory}>
                  <div className={pageStyles.noHistoryIcon}><EditIcon size={48} /></div>
                  <h3>还没有收藏的笔记</h3>
                  <p>在阅读时使用AI助手并点击心形按钮即可收藏AI回复</p>
                </div>
              )}
            />
          )}
        </HistoryDetailView>
      )}
    </div>
  );
};

export default History;
